import { useQuery } from "@tanstack/react-query";
import React, { useState, useEffect, useMemo } from "react";
import { useUser } from "../contexts/useUser";
import { fetchLLMCallsRecursive } from "../dataaccess/fetchLLMCalls";
import { BarChart } from "@mui/x-charts/BarChart";
import {
  Box,
  Container,
  Stack,
  Typography,
  useTheme,
  Paper,
} from "@mui/material";
import {
  BarPlot,
  ChartContainer,
  ChartsLegend,
  ChartsXAxis,
  ChartsYAxis,
  LineChart,
  LinePlot,
  MarkPlot,
  ResponsiveChartContainer,
  SparkLineChart,
} from "@mui/x-charts";

interface AggregatedData {
  time: string;
  count: number;
  sumThumbsUp: number;
  sumThumbsDown: number;
  notesThumbsUp: string[];
  notesThumbsDown: string[];
  errorCount: number;
  cacheCount: number;
}

interface CustomerSatProps {}

const CustomerSat: React.FC<CustomerSatProps> = () => {
  const [chartData, setChartData] = useState<AggregatedData[]>([]);
  const { project } = useUser();
  const [chartType, setChartType] = useState<"hours" | "days" | "weeks">(
    "days"
  );

  const theme = useTheme();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["calls", { projectId: project?.id }],
    queryFn: () =>
      fetchLLMCallsRecursive(project?.id ?? "", 10000, true, "", "", 30),

    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  const callRecords = data?.data?.callsByProjectIdAndCreatedAt?.items ?? [];

  const aggregateData = (
    interval: "hours" | "days" | "weeks",
    filterByServiceGroup: string = ""
  ): AggregatedData[] => {
    const aggregatedData: {
      [key: string]: {
        count: number;
        sumThumbsUp: number;
        sumThumbsDown: number;
        notesThumbsUp: string[];
        notesThumbsDown: string[];
        errorCount: number;
        cacheCount: number;
      };
    } = {};

    // fill with the right blanks for desired time interval. e.g. if interval is hours, fill with 24 hours
    // if interval is days, fill with 30 days, if interval is weeks, fill with 4 weeks

    switch (interval) {
      case "hours": {
        const now = new Date();
        for (let i = 0; i <= 24; i++) {
          // Include the current hour
          const date = new Date(now);
          date.setHours(now.getHours() - i);
          const key = `${date.getFullYear()}-${
            date.getMonth() + 1
          }-${date.getDate()} ${date.getHours()}:00`;
          aggregatedData[key] = {
            count: 0,
            sumThumbsUp: 0,
            sumThumbsDown: 0,
            notesThumbsUp: [],
            notesThumbsDown: [],
            errorCount: 0,
            cacheCount: 0,
          };
        }
        break;
      }
      case "days": {
        const now = new Date();
        for (let i = 0; i <= 30; i++) {
          // Include the current day
          const date = new Date(now);
          date.setDate(now.getDate() - i);
          const key = `${date.getFullYear()}-${
            date.getMonth() + 1
          }-${date.getDate()}`;
          aggregatedData[key] = {
            count: 0,
            sumThumbsUp: 0,
            sumThumbsDown: 0,
            notesThumbsUp: [],
            notesThumbsDown: [],
            errorCount: 0,
            cacheCount: 0,
          };
        }
        break;
      }
      case "weeks": {
        const now = new Date();
        for (let i = 0; i <= 4; i++) {
          // Include the current week
          const date = new Date(now);
          date.setDate(now.getDate() - i * 7);
          const startOfWeek = new Date(
            date.setDate(date.getDate() - date.getDay())
          );
          const key = `${startOfWeek.getFullYear()}-${
            startOfWeek.getMonth() + 1
          }-${startOfWeek.getDate()}`;
          aggregatedData[key] = {
            count: 0,
            sumThumbsUp: 0,
            sumThumbsDown: 0,
            notesThumbsUp: [],
            notesThumbsDown: [],
            errorCount: 0,
            cacheCount: 0,
          };
        }
        break;
      }
      default: {
        break;
      }
    }

    callRecords.forEach((record: any) => {
      const date = new Date(record.createdAt);
      let key: string;

      switch (interval) {
        case "hours":
          key = `${date.getFullYear()}-${
            date.getMonth() + 1
          }-${date.getDate()} ${date.getHours()}:00`;
          break;
        case "days":
          key = `${date.getFullYear()}-${
            date.getMonth() + 1
          }-${date.getDate()}`;
          break;
        case "weeks":
          const startOfWeek = new Date(
            date.setDate(date.getDate() - date.getDay())
          );
          key = `${startOfWeek.getFullYear()}-${
            startOfWeek.getMonth() + 1
          }-${startOfWeek.getDate()}`;
          break;
        default:
          key = `${date.getFullYear()}-${
            date.getMonth() + 1
          }-${date.getDate()}`;
      }

      if (!aggregatedData[key]) {
        // skip
      } else {
        aggregatedData[key].count += 1;
        if (record.status !== "200") {
          if (record.status === "302") aggregatedData[key].cacheCount += 1;
          else aggregatedData[key].errorCount += 1;
        }

        if (record.quality > 0) {
          aggregatedData[key].sumThumbsUp += record.quality;
          aggregatedData[key].notesThumbsUp.push(record.notes);
        } else {
          aggregatedData[key].sumThumbsDown += record.quality;
          aggregatedData[key].notesThumbsDown.push(record.notes);
        }
      }
    });

    return Object.keys(aggregatedData)
      .map((key) => ({
        time: key,
        count: aggregatedData[key].count,
        sumThumbsUp: aggregatedData[key].sumThumbsUp,
        sumThumbsDown: aggregatedData[key].sumThumbsDown,
        notesThumbsUp: aggregatedData[key].notesThumbsUp,
        notesThumbsDown: aggregatedData[key].notesThumbsDown,
        errorCount: aggregatedData[key].errorCount,
        cacheCount: aggregatedData[key].cacheCount,
      }))
      .reverse();
  };

  const aggregatedChartData = useMemo(() => {
    return aggregateData(chartType);
  }, [callRecords, chartType]);

  const valueFormatter = (date: string, context: any) => {
    if (context.location === "tick") {
      const dateObj = new Date(date);
      const dayOfWeek = dateObj.toLocaleString("en-US", { weekday: "short" });
      const day = dateObj.getDate();
      const month = dateObj.toLocaleString("en-US", { month: "short" });

      switch (chartType) {
        case "hours":
          return `${dayOfWeek}\n${day} ${month}\n${dateObj.getHours()}:00`;
        case "days":
          return `${dayOfWeek}\n${day} ${month}`;
        case "weeks":
          return `${dayOfWeek}\n${day} ${month}`;
      }
    }

    return `${date}`;
  };

  return (
    <>
      <Stack direction="row" sx={{ width: "100%" }} spacing={2}>
        <Paper
          sx={{
            flexGrow: 1,
            textAlign: "center",
            p: 2,
            borderRadius: 3,
            border: "1px solid #444",
          }}
        >
          <Typography variant="h6">LLM Calls</Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            {["weeks", "days", "hours"].map((interval) => (
              <Box
                key={interval}
                sx={{
                  borderColor: chartType === interval ? "#333333" : "grey.400",
                  cursor: "pointer",
                  backgroundColor:
                    chartType === interval ? "#333333" : "transparent",
                }}
                onClick={() =>
                  setChartType(interval as "hours" | "days" | "weeks")
                }
              >
                <Typography variant="button">{interval}</Typography>
              </Box>
            ))}
          </Stack>
          <BarChart
            xAxis={[
              {
                scaleType: "band",
                data: aggregatedChartData.map((data) => data.time),
                valueFormatter: valueFormatter,
                tickLabelPlacement: "tick",
              },
            ]}
            series={[
              {
                data: aggregatedChartData.map((data) => data.count),
                label: "Calls",
                color: theme.palette.primary.main,
              },
              {
                data: aggregatedChartData.map((data) => data.cacheCount),
                label: "Cache hits",
                color: "lightgreen",
              },
              {
                data: aggregatedChartData.map((data) => data.errorCount),
                label: "Errors",
                color: theme.palette.secondary.main,
              },
            ]}
            height={300}
          />
        </Paper>

        <Paper
          sx={{
            flexGrow: 1,
            textAlign: "center",
            p: 2,
            borderRadius: 3,
            border: "1px solid #444",
          }}
        >
          <Typography variant="h6">Reported Response Quality</Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            {["weeks", "days", "hours"].map((interval) => (
              <Box
                key={interval}
                sx={{
                  borderColor: chartType === interval ? "#333333" : "grey.400",
                  cursor: "pointer",
                  backgroundColor:
                    chartType === interval ? "#333333" : "transparent",
                }}
                onClick={() =>
                  setChartType(interval as "hours" | "days" | "weeks")
                }
              >
                <Typography variant="button">{interval}</Typography>
              </Box>
            ))}
          </Stack>
          <LineChart
            title="Response Satisfaction"
            xAxis={[
              {
                scaleType: "band",
                data: aggregatedChartData.map((data) => data.time),
                valueFormatter: valueFormatter,
                tickLabelPlacement: "tick",
              },
            ]}
            series={[
              {
                data: aggregatedChartData.map((data) => data.sumThumbsDown),
                label: "Bad",
                color: theme.palette.warning.light,
              },
              {
                data: aggregatedChartData.map((data) => data.sumThumbsUp),
                label: "Good",
                color: theme.palette.success.light,
              },
              {
                data: aggregatedChartData.map(
                  (data) => data.sumThumbsUp + data.sumThumbsDown
                ),
                label: "Total",
                color: theme.palette.primary.main,
              },
            ]}
            height={300}
          />
        </Paper>
      </Stack>
    </>
  );
};

export default CustomerSat;
